/**
 * Seeded chance for the idle world's bonuses (D244). Pure: a number from the world's seed and a
 * slot, the same every time it is asked. No wall clock and no global random.
 */
import type { IdleBonusKindT, IdleContent } from '@hedgerow/data';
import type { IdleWorldState } from './state.ts';

/** The world's seed: set on new worlds, or taken from when an older world was made. */
export function idleSeed(state: IdleWorldState): number {
  return (state.seed ?? state.createdAt) >>> 0;
}

/** A number in [0, 1) from a seed and two integers (a kind of bonus and a slot). */
export function idleChance(seed: number, kind: number, slot: number): number {
  let h = (seed ^ Math.imul(kind + 1, 0x9e3779b1) ^ Math.imul(slot | 0, 0x85ebca6b)) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x7feb352d) >>> 0;
  h = Math.imul(h ^ (h >>> 15), 0x846ca68b) >>> 0;
  h = (h ^ (h >>> 16)) >>> 0;
  return h / 2 ** 32;
}

export const LUCK_MARKET = 1;
export const LUCK_FIND = 2;
export const LUCK_WEATHER = 3;
/** D289: when a visitor calls, and which one. */
export const LUCK_VISITOR = 4;
export const LUCK_VISITOR_WHO = 5;
/** D304: which producers the day's orders ask for. */
export const LUCK_ORDERS = 6;

/**
 * True when good weather comes on a check-in at `now`: the player was away at least the set
 * time, and the seeded chance for the visit they left at falls under the set chance.
 */
export function idleWeatherDue(state: IdleWorldState, content: IdleContent, now: number): boolean {
  if (!idleBonusOn(content, 'weather')) return false;
  const w = content.bonuses.weather;
  if (now - state.lastSeen < w.minAwayMs) return false;
  return idleChance(idleSeed(state), LUCK_WEATHER, Math.floor(state.lastSeen / 1000)) < w.chance;
}

/** D348: whether the theme offers this kind of bonus at all (`bonuses.kinds`). */
export function idleBonusOn(content: IdleContent, kind: IdleBonusKindT): boolean {
  return content.bonuses.kinds.includes(kind);
}

/** How often a kind of offer comes and how long each stays. */
export interface IdleSlot {
  every: number;
  jitter: number;
  lasts: number;
}

/** When slot `j` of this kind opens. */
export function idleSlotOpens(
  state: IdleWorldState,
  kind: number,
  slot: IdleSlot,
  j: number,
): number {
  return (
    state.createdAt +
    j * slot.every +
    Math.floor((idleChance(idleSeed(state), kind, j) - 0.5) * 2 * slot.jitter)
  );
}

/** The slot of this kind open at `now`, if any: its number and when it goes. */
export function idleOpenSlot(
  state: IdleWorldState,
  kind: number,
  slot: IdleSlot,
  now: number,
): { k: number; until: number } | undefined {
  const since = now - state.createdAt;
  if (since < 0) return undefined;
  const k = Math.floor(since / slot.every);
  // Jitter moves an offer up to `jitter` either side of its slot's start, and an offer can
  // outlast the next slot's start, so the slots either side are checked too.
  for (const j of [k + 1, k, k - 1]) {
    if (j < 1) continue;
    const opens = idleSlotOpens(state, kind, slot, j);
    if (now >= opens && now < opens + slot.lasts) return { k: j, until: opens + slot.lasts };
  }
  return undefined;
}

/**
 * Lucky finds the player missed by going away (D254): each was on offer while the player was
 * here, was not claimed, and was still on the map when the player stopped counting as here, so
 * it went while they were away. A find that came and went while the player stayed was seen and
 * left, and pays nothing. The player was last here at `state.lastSeen`, so they count as here
 * until `lastSeen + presentMs`.
 */
export function idleMissedFindSlots(
  state: IdleWorldState,
  content: IdleContent,
  now: number,
): number[] {
  if (!idleBonusOn(content, 'lucky_find')) return [];
  const f = content.bonuses.luckyFind;
  const slot = { every: f.everyMs, jitter: f.jitterMs, lasts: f.lastsMs };
  const seen = state.lastSeen;
  const present = Math.min(now, seen + content.bonuses.presentMs);
  const claimed = state.claimed ?? [];
  const from = Math.max(
    1,
    Math.floor((seen - slot.lasts - slot.jitter - state.createdAt) / slot.every),
  );
  const to = Math.floor((present + slot.jitter - state.createdAt) / slot.every) + 1;
  const out: number[] = [];
  for (let j = from; j <= to; j++) {
    const opens = idleSlotOpens(state, LUCK_FIND, slot, j);
    const goes = opens + slot.lasts;
    const left = seen + content.bonuses.presentMs;
    if (opens < present && goes > left && goes <= now && !claimed.includes(`lucky_find:${j}`))
      out.push(j);
  }
  return out;
}
